// src/components/DocumentViewer.tsx
import React from 'react';
import { X, Download, ExternalLink } from 'lucide-react';

interface DocumentViewerProps {
  file: {
    name: string;
    url: string;
    type: string;
  };
  onClose: () => void;
}

export function DocumentViewer({ file, onClose }: DocumentViewerProps) {
  const isPDF = file.type === 'application/pdf' || file.name.endsWith('.pdf');
  const isImage = file.type.startsWith('image/');
  const isText = file.type.startsWith('text/') || file.name.endsWith('.txt');
  const isDoc = file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx');
  const isPPT = file.type.includes('presentation') || file.name.endsWith('.ppt') || file.name.endsWith('.pptx');

  // Check if it's a base64 data URL
  const isDataURL = file.url.startsWith('data:');

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl h-[90vh] bg-slate-900 rounded-2xl border-2 border-amber-400/30 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white truncate max-w-md">{file.name}</h2>
            <p className="text-xs text-amber-100">{file.type || 'Unknown type'}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={file.url}
              download={file.name}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5 text-white" />
            </a>
            {!isDataURL && (
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-5 h-5 text-white" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-800 flex items-center justify-center p-4">
          {isImage ? (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={file.url}
                alt={file.name}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  console.error('Image failed to load');
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          ) : isPDF && isDataURL ? (
            <div className="w-full h-full bg-white rounded-lg overflow-hidden">
              <iframe
                src={file.url}
                className="w-full h-full border-0"
                title={file.name}
                sandbox="allow-same-origin"
              />
            </div>
          ) : isText && isDataURL ? (
            <div className="w-full h-full bg-white rounded-lg p-6 overflow-auto">
              <iframe
                src={file.url}
                className="w-full h-full border-0"
                title={file.name}
                sandbox="allow-same-origin"
              />
            </div>
          ) : isDoc || isPPT ? (
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg className="w-10 h-10 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-white">
                <p className="text-xl font-bold mb-2">{file.name}</p>
                <p className="text-slate-300 mb-4">
                  {isDoc ? 'Word Document' : 'PowerPoint Presentation'} preview not available in browser
                </p>
                <p className="text-sm text-slate-400 mb-6">
                  Download the file to view it in {isDoc ? 'Microsoft Word' : 'PowerPoint'}
                </p>
              </div>
              <a
                href={file.url}
                download={file.name}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/50 transition-all flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download File
              </a>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center">
                <svg className="w-10 h-10 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-white">
                <p className="text-xl font-bold mb-2">{file.name}</p>
                <p className="text-slate-300 mb-6">Preview not available for this file type</p>
              </div>
              <a
                href={file.url}
                download={file.name}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-amber-500/50 transition-all flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
